import { ERROR_CODE } from "@/plugins/common/constant/error.constant";
import { INTEGRATOR_APP_FEATURE_CATEGORY } from "@/modules/app/service/app.constant";
import { HTTP_ERROR_CATEGORY } from "@/utils/http.error";

type BannerKindType = "error" | "warning" | "info";
type AppFeatureType =
  (typeof INTEGRATOR_APP_FEATURE_CATEGORY)[keyof typeof INTEGRATOR_APP_FEATURE_CATEGORY];

export interface LlmBannerInfo {
  titleKey: string;
  subtitleKey: string;
  bannerKind: BannerKindType;
}

interface FallbackBannerInfo {
  titleKey: string;
  subtitleKey: string;
}

interface ErrorBannerMapping {
  keySuffix: string;
  bannerKind: BannerKindType;
}

const errorBannerMappings: Record<string, ErrorBannerMapping> = {
  [ERROR_CODE.ACCESS_DENIED]: {
    keySuffix: "app_permission",
    bannerKind: "info",
  },
  [HTTP_ERROR_CATEGORY.ACCESS_CONFIGURATION_ISSUE]: {
    keySuffix: "access_configuration",
    bannerKind: "error",
  },
  [HTTP_ERROR_CATEGORY.INVALID_REQUEST]: {
    keySuffix: "request_issue",
    bannerKind: "warning",
  },
  [HTTP_ERROR_CATEGORY.INVALID_RESPONSE]: {
    keySuffix: "invalid_response",
    bannerKind: "warning",
  },
  [HTTP_ERROR_CATEGORY.RATE_LIMIT_REACHED]: {
    keySuffix: "usage_limit",
    bannerKind: "warning",
  },
  [HTTP_ERROR_CATEGORY.REQUEST_TIMEOUT]: {
    keySuffix: "request_timeout",
    bannerKind: "warning",
  },
  [HTTP_ERROR_CATEGORY.SERVICE_TEMPORARILY_UNAVAILABLE]: {
    keySuffix: "temporary_access",
    bannerKind: "warning",
  },
};

const appFeatureKeyByType: Record<string, string> = {
  [INTEGRATOR_APP_FEATURE_CATEGORY.LA_POST]: "conversation_la_discussion",
  [INTEGRATOR_APP_FEATURE_CATEGORY.LA_TEAM]: "conversation_la_discussion",
  [INTEGRATOR_APP_FEATURE_CATEGORY.LA_TEXT_QUERY]: "conversation_la_discussion",
  [INTEGRATOR_APP_FEATURE_CATEGORY.SA]: "sa",
  [INTEGRATOR_APP_FEATURE_CATEGORY.AFA]: "afa",
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_MODULE]: "acp_module",
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_ACTIVITY]: "acp_activity",
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT]: "acp_component",
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT_RE]: "acp_component_re",
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_RUBRIC]: "acp_rubric",
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5]: "acp_html5",
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_IMAGE]: "acp_image",
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_GAMIFICATION]: "acp_gamification",
  [INTEGRATOR_APP_FEATURE_CATEGORY.DA_POST]: "da_post",
  [INTEGRATOR_APP_FEATURE_CATEGORY.DA_AGG_FRQ]: "da_agg_frq",
  [INTEGRATOR_APP_FEATURE_CATEGORY.DA_AGG_QUIZ]: "da_agg_quiz",
  [INTEGRATOR_APP_FEATURE_CATEGORY.DA_QUIZ]: "da_quiz",
};

const genericErrorBannerInfo: FallbackBannerInfo = {
  titleKey: "api_action_generic_error_title",
  subtitleKey: "api_action_generic_error_subtitle",
};

const conversationErrorBannerInfo: FallbackBannerInfo = {
  titleKey: "conversation_la_discussion_error_banner_title",
  subtitleKey: "conversation_la_discussion_error_banner_subtitle",
};

const dataAssistantErrorBannerInfo: FallbackBannerInfo = {
  titleKey: "data_assistant_error_header",
  subtitleKey: "data_assistant_error_message",
};

const fallbackBannerInfoByFeature: Record<AppFeatureType, FallbackBannerInfo> = {
  [INTEGRATOR_APP_FEATURE_CATEGORY.LA_POST]: conversationErrorBannerInfo,
  [INTEGRATOR_APP_FEATURE_CATEGORY.LA_TEAM]: conversationErrorBannerInfo,
  [INTEGRATOR_APP_FEATURE_CATEGORY.DA_POST]: dataAssistantErrorBannerInfo,
  [INTEGRATOR_APP_FEATURE_CATEGORY.DA_AGG_FRQ]: dataAssistantErrorBannerInfo,
  [INTEGRATOR_APP_FEATURE_CATEGORY.DA_AGG_QUIZ]: dataAssistantErrorBannerInfo,
  [INTEGRATOR_APP_FEATURE_CATEGORY.DA_QUIZ]: dataAssistantErrorBannerInfo,
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_MODULE]: {
    titleKey: "lesson_module_authoring_copilot_result_error_title",
    subtitleKey: "lesson_module_authoring_copilot_result_error_content",
  },
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_ACTIVITY]: {
    titleKey: "lesson_activity_authoring_copilot_wait_error_title",
    subtitleKey: "lesson_activity_authoring_copilot_wait_error_content",
  },
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT]: {
    titleKey: "lesson_component_authoring_copilot_wait_error_title",
    subtitleKey: "lesson_component_authoring_copilot_wait_error_content",
  },
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT_RE]: {
    titleKey: "lesson_component_regen_authoring_copilot_wait_error_title",
    subtitleKey: "lesson_component_regen_authoring_copilot_wait_error_content",
  },
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_RUBRIC]: {
    titleKey: "lesson_rubric_authoring_copilot_wait_error_title",
    subtitleKey: "lesson_rubric_authoring_copilot_wait_error_content",
  },
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5]: {
    titleKey: "acp_html5_generation_content_subpage_error_banner_title",
    subtitleKey: "acp_generation_content_subpage_error_banner_subtitle",
  },
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_IMAGE]: {
    titleKey: "acp_image_content_subpage_error_banner_title",
    subtitleKey: "acp_generation_content_subpage_error_banner_subtitle",
  },
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_GAMIFICATION]: {
    titleKey: "acp_gamification_generation_content_subpage_processing_error_banner_title",
    subtitleKey: "acp_generation_content_subpage_error_banner_subtitle",
  },
};

export const hasErrorBannerMapping = (errorCode: string | undefined): boolean =>
  errorCode !== undefined && Object.prototype.hasOwnProperty.call(errorBannerMappings, errorCode);

export const getErrorBannerInfo = (
  appFeatureType: string | null | undefined,
  errorCode: string | undefined,
): LlmBannerInfo => {
  const appFeatureKey = appFeatureType ? appFeatureKeyByType[appFeatureType] : undefined;
  const errorBannerMapping = errorCode ? errorBannerMappings[errorCode] : undefined;

  if (appFeatureKey && errorBannerMapping) {
    return {
      titleKey: `${appFeatureKey}_error_${errorBannerMapping.keySuffix}_title`,
      subtitleKey: `${appFeatureKey}_error_${errorBannerMapping.keySuffix}_subtitle`,
      bannerKind: errorBannerMapping.bannerKind,
    };
  }

  return {
    ...(fallbackBannerInfoByFeature[appFeatureType ?? ""] ?? genericErrorBannerInfo),
    bannerKind: "error",
  };
};
